import { useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkConflicts,
  getBetSlipFromLs,
  updateBaseEventsData
} from 'utils/betslip/betslip';
import LS from 'utils/bom-dom-manipulation/local-storage';
import { PRIVATE_COMPETITIONS } from 'utils/constants/sportsbook/private-competitions';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { betslipBetsCount } from 'utils/sportsbook/api-post-message-sender';
import { getGameStatus } from 'utils/sportsbook/game-current-state';
import { formatPrice } from 'utils/string-manipulation/format-price';
import { BetSlipEventObj } from 'interfaces/bet-data';
import {
  BetslipEvents,
  EBetslipBetType,
  SportBonusResponce,
  TSuspendReason
} from 'interfaces/betslip';
import { getEachWay } from 'services/get-each-way';
import { getSportBonusRules } from 'services/sportsbook/sport-bonus-rules';
import { Event } from '../../../../interfaces/sportsbook-data-levels';
import { executeCommand } from '../../../../services/get-data-level';
import RidGenerator from '../../../../utils/swarm/rid-generator';
import { calculateAndDispatchUpdates } from 'newelements/_Betslip/utils/action-middlewares/store-actions-middlewares/calculateAndDispatchUpdates';
import { dispatchBetslipAllBets } from 'newelements/_Betslip/utils/action-middlewares/store-actions-middlewares/dispatchBetslipAllBets';
import { GENERAL_FIELD_KEY } from 'newelements/_Betslip/utils/constants/shared';
import { setSportBonusRules } from 'store/actions';
import {
  getAllLsBets,
  getComputedBetType,
  getSportBonuses,
  getSubscribedEvents
} from 'store/selectors/betslip';
import { getIsLoggedIn } from 'store/selectors/user-data';

export const BetsManager = (): null => {
  const dispatch = useDispatch();

  const allLsBets = useSelector(getAllLsBets);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const betType = useSelector(getComputedBetType);
  const sportBonuses = useSelector(getSportBonuses);
  const subscribedEvents = useSelector(getSubscribedEvents);

  useLayoutEffect(() => {
    getSportBonusRules((data: SportBonusResponce) => {
      if (data?.details) {
        dispatch(setSportBonusRules(data.details));
      }
    });

    allLsBets.forEach(event => {
      if (event.each_way) {
        getEachWay({ market_id: event?.market?.id });
      }
    });
  }, []);

  useLayoutEffect(() => {
    const isMultiples = betType !== EBetslipBetType.SINGLE;

    if (sportBonuses.length && isMultiples) {
      calculateAndDispatchUpdates({ id: GENERAL_FIELD_KEY, stake: '' });
    }
  }, [sportBonuses]);

  useLayoutEffect(() => {
    if (SpringConfigs.IFRAME_SPORTSBOOK && allLsBets) {
      betslipBetsCount(allLsBets.length || 0);
    }
  }, [allLsBets.length]);

  useEffect(() => {
    const eventIds = Object.keys(subscribedEvents);

    if (eventIds.length) {
      const bets: BetSlipEventObj[] = [];
      const suspendedEventsData: Record<string, TSuspendReason[]> = {};
      getBetSlipFromLs().bets.forEach((bet: BetSlipEventObj) => {
        if (bet.isFakeTicket) {
          bets.push(bet);

          return;
        }

        const newEvent = subscribedEvents[
          bet.eventId
        ] as BetslipEvents['value'];

        if (newEvent) {
          if (
            SpringConfigs.BETSLIP_BASE_UPDATE &&
            newEvent.base &&
            newEvent.suspendReasons &&
            newEvent.suspendReasons[0] === 'market' &&
            bet.sport_alias?.toLowerCase() === 'basketball'
          ) {
            const rid = RidGenerator.gForSubscribe();
            executeCommand(
              rid,
              {
                event: ['id', 'name', 'base', 'price']
              },
              {
                game: {
                  id: bet.gameId
                },
                market: {
                  type: bet.marketType
                },
                event: {
                  type_1: bet.type_1
                }
              },
              (data: { event: Record<string, Event> }) => {
                updateBaseEventsData(data, bet, false, false);
              }
            );
          }

          const shouldHidePrivateCompetitionGame =
            !isLoggedIn &&
            newEvent.competitionId &&
            PRIVATE_COMPETITIONS[SpringConfigs.JURISDICTION]?.includes(
              newEvent.competitionId
            );

          if (shouldHidePrivateCompetitionGame) {
            bet.current_game_state = '';

            if (bet.game_info) {
              bet.game_info.score1 = '-';
              bet.game_info.score2 = '-';
            }
          } else {
            bet.current_game_state =
              newEvent.game_state ?? bet.current_game_state;
            bet.current_game_time = newEvent.game_time ?? bet.current_game_time;

            if (bet.game_info && newEvent.score1 && newEvent.score2) {
              bet.game_info.score1 = newEvent.score1;
              bet.game_info.score2 = newEvent.score2;
            }
          }

          if (newEvent.base !== undefined) {
            bet.eventBase = newEvent.base;
          }

          bet.sport_alias = newEvent.sport_alias || '';

          if (newEvent.sport_id) {
            bet.sport_id = newEvent.sport_id;
          }

          bet.isSuspended = !!newEvent.suspendReasons;
          bet.suspendReasons = newEvent.suspendReasons;

          if (bet.isSuspended) {
            calculateAndDispatchUpdates({ id: bet.eventId, stake: '' });
          }

          if (newEvent.suspendReasons) {
            suspendedEventsData[bet.eventId] = newEvent.suspendReasons;
          }

          bet.coeficient =
            (newEvent.price && formatPrice(newEvent.price, '')) ||
            (bet.coeficient && formatPrice(bet.coeficient, ''));

          bet.isLive = newEvent.isLive ?? bet.isLive;

          if (bet.current_game_state) {
            bet.game_status = getGameStatus(
              bet.current_game_state ?? '',
              bet.current_game_time ?? '',
              bet.isLive,
              bet.game_start_date,
              bet.sport_index || ''
            );
          }

          bets.push(bet);
        }
      });

      LS.setItem(
        storageKeyName('betslip', 'BETSLIP_SUSPENDED_EVENTS'),
        JSON.stringify(suspendedEventsData)
      );

      if (!bets.length || SpringConfigs.MOCKED_DATA) {
        return;
      }

      checkConflicts(bets);
      dispatchBetslipAllBets(bets);
      // dispatch(setAllLsBets(bets));
    }
  }, [subscribedEvents, isLoggedIn]);

  return null;
};
